import type {
  AddExternalErrorRequest,
  ExternalErrorSeverity,
} from '@/modelsAPI/external-error';

const LOG_BUFFER_MAX = 50;
const MAX_FIELD_LEN = 8 * 1024;

// Cross-origin "Script error." reports carry no usable information — only every Nth is sent.
const CROSS_ORIGIN_SAMPLE_ONE_IN = 100;
// Same error signature is reported at most once per window.
const DEDUP_WINDOW_MS = 5 * 60 * 1000;
const DEDUP_MAP_MAX = 100;
const DEDUP_KEY_MAX_LEN = 500;
// Sliding-window rate limit and per-page-load circuit breaker.
const RATE_WINDOW_MS = 60 * 1000;
const MAX_REPORTS_PER_MINUTE = 5;
const MAX_REPORTS_PER_SESSION = 30;

interface LogEntry {
  level: 'log' | 'info' | 'warn' | 'error' | 'debug';
  time: string;
  message: string;
}

const logBuffer: LogEntry[] = [];
let userIDProvider: (() => string | null) | null = null;
let isReporting = false;
let consoleWrapped = false;

const dedupLastSent = new Map<string, number>();
const sentTimestamps: number[] = [];
let sessionReportCount = 0;
let breakerTripped = false;
let droppedDedupCount = 0;
let droppedRateCount = 0;
let crossOriginSeenCount = 0;
let crossOriginDroppedCount = 0;

function truncate(value: string): string {
  if (value.length <= MAX_FIELD_LEN) return value;
  return value.slice(0, MAX_FIELD_LEN) + '…[truncated]';
}

function safeStringify(value: unknown): string {
  try {
    if (typeof value === 'string') return value;
    if (value instanceof Error) return `${value.name}: ${value.message}`;
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function pushLog(level: LogEntry['level'], args: unknown[]): void {
  if (isReporting) return;
  try {
    const message = truncate(args.map(safeStringify).join(' '));
    logBuffer.push({ level, time: new Date().toISOString(), message });
    if (logBuffer.length > LOG_BUFFER_MAX) {
      logBuffer.splice(0, logBuffer.length - LOG_BUFFER_MAX);
    }
  } catch {
    // ignore
  }
}

function wrapConsole(): void {
  if (consoleWrapped) return;
  consoleWrapped = true;

  const levels: LogEntry['level'][] = ['log', 'info', 'warn', 'error', 'debug'];
  for (const level of levels) {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      original(...args);
      pushLog(level, args);
    };
  }
}

wrapConsole();

export function setUserIDProvider(provider: (() => string | null) | null): void {
  userIDProvider = provider;
}

function buildDedupKey(
  severity: ExternalErrorSeverity,
  type: string,
  message: string,
  error?: unknown,
): string {
  const firstStackLines = error instanceof Error && typeof error.stack === 'string'
    ? error.stack.split('\n').slice(0, 2).join(' ')
    : '';
  return `${severity}|${type}|${message}|${firstStackLines}`.slice(0, DEDUP_KEY_MAX_LEN);
}

function sendReport(
  severity: ExternalErrorSeverity,
  type: string,
  message: string,
  error?: unknown,
  extraData?: { [_:string]: unknown },
): void {
  try {
    isReporting = true;

    const stackRaw = error instanceof Error && typeof error.stack === 'string' ? error.stack : null;
    const stack = stackRaw !== null ? truncate(stackRaw) : null;
    const truncatedMessage = truncate(message);

    let userID: string | null = null;
    try {
      userID = userIDProvider !== null ? userIDProvider() : null;
    } catch {
      userID = null;
    }

    const data = {
      ...extraData,
      url: typeof window !== 'undefined' ? window.location.href : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      stack,
      recentLogs: logBuffer.slice(),
      extra: extraData,
    };

    const body: AddExternalErrorRequest = {
      severity,
      message: truncatedMessage,
      type,
      data,
      userID,
    };

    void import('@/services/api.routes')
      .then(({ APIRoutes }) => APIRoutes.addExternalError(body))
      .catch(() => {
        // fire-and-forget — never let reporting failures bubble up
      });
  } catch {
    // ignore
  } finally {
    isReporting = false;
  }
}

export function reportError(
  severity: ExternalErrorSeverity,
  type: string,
  message: string,
  error?: unknown,
  extraData?: { [_:string]: unknown },
): void {
  try {
    if (breakerTripped) return;

    if (sessionReportCount >= MAX_REPORTS_PER_SESSION) {
      breakerTripped = true;
      sendReport('Warn', 'FRONTEND_REPORTER_LIMIT', 'Client error reporter circuit breaker tripped', undefined, {
        sessionReportCount,
        droppedDedupCount,
        droppedRateCount,
        crossOriginDroppedCount,
      });
      return;
    }

    const now = Date.now();

    const dedupKey = buildDedupKey(severity, type, message, error);
    const lastSent = dedupLastSent.get(dedupKey);
    if (lastSent !== undefined && now - lastSent < DEDUP_WINDOW_MS) {
      droppedDedupCount++;
      return;
    }

    while (sentTimestamps.length > 0 && now - sentTimestamps[0] >= RATE_WINDOW_MS) {
      sentTimestamps.shift();
    }
    if (sentTimestamps.length >= MAX_REPORTS_PER_MINUTE) {
      droppedRateCount++;
      return;
    }

    dedupLastSent.set(dedupKey, now);
    if (dedupLastSent.size > DEDUP_MAP_MAX) {
      const oldestKey = dedupLastSent.keys().next().value;
      if (oldestKey !== undefined) {
        dedupLastSent.delete(oldestKey);
      }
    }
    sentTimestamps.push(now);
    sessionReportCount++;

    sendReport(severity, type, message, error, extraData);
  } catch {
    // ignore
  }
}

export function handleCrossOriginScriptError(event: ErrorEvent): boolean {
  const hasUsableError = event.error !== null && event.error !== undefined;
  const isCrossOriginSignature =
    !hasUsableError &&
    event.message.startsWith('Script error') &&
    event.filename === '' &&
    event.lineno === 0 &&
    event.colno === 0;

  if (!isCrossOriginSignature) return false;

  crossOriginSeenCount++;
  if (crossOriginSeenCount % CROSS_ORIGIN_SAMPLE_ONE_IN === 1) {
    reportError('Warn', 'FRONTEND_CROSSORIGIN', event.message, undefined, {
      sampled: true,
      sampleOneIn: CROSS_ORIGIN_SAMPLE_ONE_IN,
      droppedSinceSessionStart: crossOriginDroppedCount,
    });
  } else {
    crossOriginDroppedCount++;
  }
  return true;
}
